import React, { useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import { Button, Card, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Header from '~/Components/Header/Header';
import AuthStorage from '~/Utils/AuthStorage';
import { CARACTERISTICA_ALTERAR_ARTE_ASSINATURA } from '~/constant';
import { salvarPdf } from '~/Utils/Helpers';
import { obterArteAssinatura, gerarAssinatura } from '~/Services/Service';
import { UNIDADES, UNIDADE_SEM_CONTATO } from './constants';
import { parsearCelular, extrairMensagemErro } from './helpers';
import { validationSchema } from './validationSchema';
import FormularioAssinatura from './components/FormularioAssinatura';
import PreviewAssinatura from './components/PreviewAssinatura';
import ModalAlterarArte from './components/ModalAlterarArte';

const valoresIniciais = {
  nome: '',
  departamento: '',
  celular: '',
};

const GeradorAssinaturaEmail = () => {
  const [unidadeId, setUnidadeId] = useState(UNIDADES[0].id);
  const [arteUrl, setArteUrl] = useState(null);
  const [carregandoArte, setCarregandoArte] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [showModalArte, setShowModalArte] = useState(false);
  const [versaoArte, setVersaoArte] = useState(0); // força refetch após "alterar arte"

  const podeAlterarArte = AuthStorage.temCaracteristicas([
    CARACTERISTICA_ALTERAR_ARTE_ASSINATURA,
  ]);

  // Busca a arte da unidade selecionada (blob autenticado -> objectURL).
  useEffect(() => {
    let ativo = true;
    setCarregandoArte(true);
    obterArteAssinatura(unidadeId)
      .then(blob => {
        if (!ativo) return;
        const url = window.URL.createObjectURL(blob);
        setArteUrl(anterior => {
          if (anterior) window.URL.revokeObjectURL(anterior);
          return url;
        });
      })
      .catch(async err => {
        if (ativo) toast.error(await extrairMensagemErro(err));
      })
      .finally(() => {
        if (ativo) setCarregandoArte(false);
      });
    return () => {
      ativo = false;
    };
  }, [unidadeId, versaoArte]);

  // Revoga o objectURL ao desmontar.
  useEffect(
    () => () => {
      setArteUrl(u => {
        if (u) window.URL.revokeObjectURL(u);
        return null;
      });
    },
    []
  );

  const handleGerar = values => {
    setGerando(true);
    const payload = {
      unidade_id: unidadeId,
      nome: values.nome,
      departamento: values.departamento,
      celular: values.celular,
    };
    gerarAssinatura(payload)
      .then(blob => salvarPdf(blob, 'Assinatura.jpg'))
      .catch(async err => {
        toast.error(await extrairMensagemErro(err));
      })
      .finally(() => setGerando(false));
  };

  return (
    <div className="bg-light min-vh-100">
      <Header title="Gerador de Assinatura de E-mail">
        {podeAlterarArte ? (
          <Button variant="outline-primary" onClick={() => setShowModalArte(true)}>
            Alterar Arte
          </Button>
        ) : null}
      </Header>

      <div className="container-fluid py-4">
        <Formik
          initialValues={valoresIniciais}
          validationSchema={validationSchema}
          onSubmit={handleGerar}
        >
          {formik => {
            // "Se digitar, é sim; se não, é não": o contato existe quando há um
            // celular válido digitado — sem checkbox/rádio para optar.
            const cel = parsearCelular(formik.values.celular);
            const celularPreenchido =
              formik.values.celular.replace(/\D/g, '').length > 0;
            const mostrarContato = !!cel && unidadeId !== UNIDADE_SEM_CONTATO;
            const podeGerar =
              formik.values.nome.trim() !== '' &&
              (!celularPreenchido || !!cel) &&
              !gerando;

            return (
              <Form onSubmit={formik.handleSubmit}>
                <Card className="mb-4">
                  <Card.Body>
                    <FormularioAssinatura
                      formik={formik}
                      unidadeId={unidadeId}
                      onChangeUnidade={setUnidadeId}
                    />
                  </Card.Body>
                </Card>

                <Card className="mb-4">
                  <Card.Header>Pré-visualização</Card.Header>
                  <Card.Body>
                    <PreviewAssinatura
                      arteUrl={arteUrl}
                      carregando={carregandoArte}
                      nome={formik.values.nome}
                      departamento={formik.values.departamento}
                      mostrarContato={mostrarContato}
                      ddd={cel ? cel.ddd : ''}
                      numero={cel ? cel.numero : ''}
                    />
                  </Card.Body>
                </Card>

                <div className="text-center">
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    disabled={!podeGerar}
                  >
                    {gerando ? (
                      <>
                        <Spinner animation="border" size="sm" /> Gerando...
                      </>
                    ) : (
                      'Gerar Assinatura'
                    )}
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>

      <ModalAlterarArte
        show={showModalArte}
        onClose={() => setShowModalArte(false)}
        onArteAlterada={idAlterada => {
          if (idAlterada === unidadeId) setVersaoArte(v => v + 1);
        }}
      />
    </div>
  );
};

export default GeradorAssinaturaEmail;
